import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Compass, RefreshCw, Sparkles, MapPin } from "lucide-react";
import { MATCH_OPTIONS, PROJECTS } from "../../mock";
import { Reveal, SectionHeading } from "../common";

function Pills({ label, options, value, onChange }) {
  return (
    <div>
      <p className="font-heading text-[12px] font-bold uppercase tracking-[0.16em] text-ivi-slateSoft">
        {label}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {options.map((o) => {
          const on = value === o;
          return (
            <button
              key={o}
              type="button"
              onClick={() => onChange(on ? null : o)}
              className={`rounded-full px-4 py-2.5 text-[14px] font-semibold transition-[background-color,color,border-color,transform] duration-200 hover:-translate-y-0.5 ${
                on
                  ? "bg-ivi-green text-white shadow-[0_10px_22px_-12px_rgba(90,158,47,1)]"
                  : "border border-ivi-mintDeep bg-white text-ivi-slate hover:border-ivi-green"
              }`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function MatchFinder() {
  const [interest, setInterest] = useState(null);
  const [region, setRegion] = useState(null);
  const [duration, setDuration] = useState(null);

  const results = useMemo(
    () =>
      PROJECTS.filter(
        (p) =>
          (!interest || p.interest === interest) &&
          (!region || p.region === region) &&
          (!duration || p.duration === duration)
      ),
    [interest, region, duration]
  );

  const reset = () => {
    setInterest(null);
    setRegion(null);
    setDuration(null);
  };

  const surprise = () => {
    const p = PROJECTS[Math.floor(Math.random() * PROJECTS.length)];
    setInterest(p.interest);
    setRegion(p.region);
    setDuration(p.duration);
  };

  return (
    <section id="match" className="relative overflow-hidden bg-ivi-mint py-24 lg:py-32">
      <div className="pointer-events-none absolute -right-24 top-10 h-72 w-72 rounded-full bg-white/60 blur-3xl" />
      <div className="relative mx-auto max-w-[1400px] px-5 lg:px-8">
        <SectionHeading
          eyebrow="Find your project match"
          title="Three taps to your perfect placement"
          text="Tell us what moves you, where you want to be and how long you have. We'll shortlist the projects that fit."
        />

        <div className="mt-14 grid gap-8 lg:grid-cols-[380px_1fr]">
          <Reveal>
            <div className="sticky top-28 rounded-3xl bg-white p-8 shadow-[0_20px_50px_-30px_rgba(24,40,32,0.5)] ring-1 ring-ivi-mintDeep">
              <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-ivi-mint text-ivi-greenDark">
                  <Compass className="h-5 w-5" />
                </span>
                <p className="font-heading text-[18px] font-bold text-ivi-slate">Your preferences</p>
              </div>

              <div className="mt-7 space-y-7">
                <Pills
                  label="I care most about"
                  options={MATCH_OPTIONS.interest}
                  value={interest}
                  onChange={setInterest}
                />
                <Pills
                  label="Region"
                  options={MATCH_OPTIONS.region}
                  value={region}
                  onChange={setRegion}
                />
                <Pills
                  label="How long"
                  options={MATCH_OPTIONS.duration}
                  value={duration}
                  onChange={setDuration}
                />
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={surprise}
                  className="inline-flex items-center gap-2 rounded-full bg-ivi-slate px-5 py-3 font-heading text-[14px] font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-ivi-slate/90"
                >
                  <Sparkles className="h-4 w-4 text-ivi-lime" /> Surprise me
                </button>
                <button
                  type="button"
                  onClick={reset}
                  className="inline-flex items-center gap-2 rounded-full border border-ivi-mintDeep px-5 py-3 font-heading text-[14px] font-bold text-ivi-slate transition-colors duration-200 hover:bg-ivi-mint"
                >
                  <RefreshCw className="h-4 w-4" /> Reset
                </button>
              </div>
            </div>
          </Reveal>

          <div>
            <div className="mb-5 flex items-baseline justify-between">
              <p className="font-heading text-[15px] font-bold text-ivi-slate">
                {results.length} {results.length === 1 ? "project" : "projects"} match
              </p>
              <p className="text-[13px] text-ivi-slateSoft">Prices in USD, excl. application fee</p>
            </div>

            {results.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-ivi-mintDeep bg-white p-14 text-center">
                <p className="font-heading text-[19px] font-bold text-ivi-slate">
                  No exact match but we&apos;ll find you one
                </p>
                <p className="mx-auto mt-3 max-w-md text-[15px] text-ivi-slateSoft">
                  Loosen one filter, or book a free 33-minute chat and our team will hand-pick
                  something for you.
                </p>
              </div>
            ) : (
              <div className="grid gap-5 sm:grid-cols-2">
                {results.map((p, i) => (
                  <Reveal key={p.id} delay={i * 60}>
                    <Link
                      to="/projects/volunteer-in-bali"
                      className="group flex h-full flex-col overflow-hidden rounded-3xl bg-white shadow-[0_12px_34px_-24px_rgba(24,40,32,0.5)] ring-1 ring-ivi-mintDeep transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_28px_54px_-26px_rgba(24,40,32,0.5)]"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={p.img}
                          alt={p.name}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-110"
                        />
                        {p.popular && (
                          <span className="absolute left-4 top-4 rounded-full bg-ivi-lime px-3 py-1 font-heading text-[11px] font-extrabold uppercase tracking-[0.14em] text-ivi-slate">
                            Most popular
                          </span>
                        )}
                        <span className="absolute bottom-3 right-3 rounded-full bg-white/95 px-3 py-1.5 font-heading text-[13px] font-extrabold text-ivi-greenDark">
                          ${p.price}
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col p-6">
                        <p className="flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.14em] text-ivi-slateSoft">
                          <MapPin className="h-3.5 w-3.5 text-ivi-green" /> {p.place}
                        </p>
                        <h3 className="mt-2 font-heading text-[19px] font-bold leading-snug text-ivi-slate">
                          {p.name}
                        </h3>
                        <p className="mt-3 flex-1 text-[14px] leading-relaxed text-ivi-slateSoft">
                          {p.blurb}
                        </p>
                        <div className="mt-5 flex flex-wrap gap-2">
                          <span className="rounded-full bg-ivi-mint px-3 py-1 text-[12px] font-semibold text-ivi-greenDark">
                            {p.duration}
                          </span>
                          <span className="rounded-full bg-ivi-sand px-3 py-1 text-[12px] font-semibold text-ivi-slateSoft">
                            {p.region}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
